import { ChevronDown, Loader2, Pencil, Plug, PlugZap, RefreshCw, Trash2, Wrench } from "lucide-react"
import * as React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import { PageHeader } from "@/components/layout"
import { ConnectionStatusBadge } from "@/components/status-badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { AuthType, Connection, McpTool } from "@/lib/types"
import { formatRelative } from "@/lib/utils"

/**
 * Admin-only management of another user's MCP connections: full CRUD plus the live
 * probe/refresh/test the user has on their own Connections page. A self-contained
 * sibling of `connections` wired to the admin endpoints (so the server is owned by
 * the target user), with an extra Edit action the self-service page does not expose.
 */
export function AdminUserConnectionsPage() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [connections, setConnections] = React.useState<Connection[]>([])
  const [loading, setLoading] = React.useState(true)
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Connection | null>(null)

  const load = React.useCallback(async () => {
    if (!userId) return
    try {
      setConnections(await api.adminUserConnections(userId))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not load connections.")
    } finally {
      setLoading(false)
    }
  }, [userId])

  React.useEffect(() => {
    void load()
  }, [load])

  if (!userId) return null

  return (
    <>
      <PageHeader
        title="Tool servers"
        description="Create and manage this user's MCP servers on their behalf."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/admin/users/${userId}`)}>
              Back to user
            </Button>
            <Button
              onClick={() => {
                setEditing(null)
                setDialogOpen(true)
              }}
            >
              Add server
            </Button>
          </div>
        }
      />

      <div className="space-y-4 p-6">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : connections.length === 0 ? (
          <EmptyState
            icon={Plug}
            title="No tool servers"
            description="Register an MCP server to give this user's agents tools."
            actionLabel="Add server"
            onAction={() => {
              setEditing(null)
              setDialogOpen(true)
            }}
          />
        ) : (
          connections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              userId={userId}
              connection={connection}
              onChange={load}
              onEdit={() => {
                setEditing(connection)
                setDialogOpen(true)
              }}
            />
          ))
        )}
      </div>

      <ConnectionDialog
        userId={userId}
        open={dialogOpen}
        connection={editing}
        onOpenChange={setDialogOpen}
        onSaved={load}
      />
    </>
  )
}

function ConnectionCard({
  userId,
  connection,
  onChange,
  onEdit,
}: {
  userId: string
  connection: Connection
  onChange: () => Promise<void>
  onEdit: () => void
}) {
  const { locale } = useLocale()
  const [tools, setTools] = React.useState<McpTool[] | null>(null)
  const [busy, setBusy] = React.useState<string | null>(null)

  async function withBusy(label: string, work: () => Promise<void>) {
    setBusy(label)
    try {
      await work()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Action failed.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{connection.name}</CardTitle>
          <ConnectionStatusBadge status={connection.status} enabled={connection.enabled} />
          <Badge variant="outline">{connection.transport}</Badge>
          {connection.auth.has_secret && (
            <Badge variant="secondary">
              {connection.auth.type} {connection.auth.hint}
            </Badge>
          )}
        </div>
        <CardDescription className="break-all">{connection.url}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {connection.last_error && (
          <Alert variant="destructive">
            <AlertTitle>Last attempt failed</AlertTitle>
            <AlertDescription>{connection.last_error}</AlertDescription>
          </Alert>
        )}

        <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span>
            <Wrench className="mr-1 inline size-3.5" />
            {connection.tool_count} tools
          </span>
          {connection.server_name && (
            <span>
              {connection.server_name}
              {connection.server_version ? ` ${connection.server_version}` : ""}
            </span>
          )}
          <span>Checked {formatRelative(connection.last_checked_at, locale)}</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              withBusy("test", async () => {
                await api.adminTestUserConnection(userId, connection.id)
                toast.success(`${connection.name} is reachable.`)
                await onChange()
              })
            }
          >
            {busy === "test" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <PlugZap className="size-4" />
            )}
            Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              withBusy("refresh", async () => {
                const result = await api.adminRefreshUserConnection(userId, connection.id)
                toast.success(
                  `Catalog updated: +${result.added} / ~${result.updated} / -${result.removed}.`,
                )
                setTools(result.tools)
                await onChange()
              })
            }
          >
            {busy === "refresh" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            Refresh tools
          </Button>

          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (tools === null) {
                    try {
                      setTools(await api.adminUserConnectionTools(userId, connection.id))
                    } catch {
                      toast.error("Could not load tools.")
                    }
                  }
                }}
              >
                <ChevronDown className="size-4" />
                Browse tools
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <ToolList tools={tools} />
            </CollapsibleContent>
          </Collapsible>

          <Button variant="ghost" size="sm" className="ml-auto" onClick={onEdit}>
            <Pencil className="size-4" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            disabled={busy !== null}
            onClick={() =>
              withBusy("delete", async () => {
                if (!confirm(`Remove ${connection.name}?`)) return
                await api.adminDeleteUserConnection(userId, connection.id)
                toast.success(`${connection.name} removed.`)
                await onChange()
              })
            }
          >
            <Trash2 className="size-4" />
            Remove
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ToolList({ tools }: { tools: McpTool[] | null }) {
  if (tools === null) return <Skeleton className="h-20 w-full" />
  if (tools.length === 0) {
    return <p className="text-muted-foreground text-sm">No tools discovered.</p>
  }
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {tools.map((tool) => (
        <div key={tool.id} className="rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="payload font-medium">{tool.name}</span>
            {tool.unsupported_reason && <Badge variant="warning">unsupported</Badge>}
          </div>
          {tool.description && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{tool.description}</p>
          )}
          <p className="text-muted-foreground payload mt-2 opacity-70">Calls as {tool.llm_name}</p>
        </div>
      ))}
    </div>
  )
}

function ConnectionDialog({
  userId,
  open,
  connection,
  onOpenChange,
  onSaved,
}: {
  userId: string
  open: boolean
  connection: Connection | null
  onOpenChange: (open: boolean) => void
  onSaved: () => Promise<void>
}) {
  const isEdit = connection !== null
  const [name, setName] = React.useState("")
  const [url, setUrl] = React.useState("")
  const [authType, setAuthType] = React.useState<AuthType>("none")
  const [headerName, setHeaderName] = React.useState("")
  const [secret, setSecret] = React.useState("")
  const [busy, setBusy] = React.useState(false)

  // Re-seed the form whenever the dialog opens, for the connection being edited (or a
  // blank create). Secrets are write-only, so the field always starts empty.
  React.useEffect(() => {
    if (!open) return
    setName(connection?.name ?? "")
    setUrl(connection?.url ?? "")
    setAuthType((connection?.auth.type as AuthType) ?? "none")
    setHeaderName(connection?.auth.header_name ?? "")
    setSecret("")
  }, [open, connection])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      if (isEdit && connection) {
        await api.adminUpdateUserConnection(userId, connection.id, {
          name,
          url,
          auth: {
            type: authType,
            header_name: authType === "header" ? headerName : null,
            // Blank leaves the stored secret untouched; "none" clears it server-side.
            secret: authType === "none" ? "" : secret === "" ? null : secret,
          },
        })
        toast.success(`Saved "${name}".`)
      } else {
        const result = await api.adminCreateUserConnection(userId, {
          name,
          url,
          auth: {
            type: authType,
            header_name: authType === "header" ? headerName : null,
            secret: authType === "none" ? null : secret,
          },
        })
        toast.success(`Connected with ${result.tools.length} tools.`)
      }
      onOpenChange(false)
      await onSaved()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not save the connection.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit tool server" : "Add tool server"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this server. Leave the secret blank to keep the stored credential."
              : "Register an MCP server. It is probed immediately to load its tools."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="conn-name">Name</Label>
            <Input
              id="conn-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Internal Tools"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="conn-url">URL</Label>
            <Input
              id="conn-url"
              required
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://tools.example.com/mcp"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="conn-auth">Authentication</Label>
            <Select value={authType} onValueChange={(value) => setAuthType(value as AuthType)}>
              <SelectTrigger id="conn-auth">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="bearer">Bearer token</SelectItem>
                <SelectItem value="header">Custom header</SelectItem>
                <SelectItem value="basic">Basic auth</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {authType === "header" && (
            <div className="space-y-2">
              <Label htmlFor="conn-header">Header name</Label>
              <Input
                id="conn-header"
                required
                value={headerName}
                onChange={(event) => setHeaderName(event.target.value)}
                placeholder="X-API-Key"
              />
            </div>
          )}

          {authType !== "none" && (
            <div className="space-y-2">
              <Label htmlFor="conn-secret">
                {authType === "basic" ? "username:password" : "Secret"}
              </Label>
              <Input
                id="conn-secret"
                type="password"
                required={!isEdit}
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
                placeholder={isEdit ? "Leave blank to keep current" : ""}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {busy ? "Saving…" : isEdit ? "Save" : "Connect"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
